<template>
  <div :class="$style.container">
    <!-- 스켈레톤 UI -->
    <div v-if="loading" :class="$style.skeletonBannerContainer">
      <div :class="$style.skeletonBanner">
        <div :class="$style.skeletonBannerImg"></div>
      </div>
    </div>

    <!-- 실제 배너 -->
    <div v-else-if="banners.length > 0" :class="$style.bannerContainer">
      <swiper :modules="[Navigation, Pagination, Autoplay]" navigation pagination loop
        :autoplay="{ delay: 6000, disableOnInteraction: false }" :class="$style.bannerSwiper">
        <swiper-slide v-for="(banner, index) in banners" :key="index" :class="$style.slide">
          <!-- <p :class="$style.dayCount">{{ banner.dayCount }}</p> -->
          <img :class="$style.bannerImg" :src="banner.img" :alt="`Banner ${index + 1}`" style="cursor: pointer"
            @click="handleBannerClick(banner)" />
        </swiper-slide>
      </swiper>
    </div>
  </div>
</template>

<script>
import { parse } from "date-fns";
import { Swiper, SwiperSlide } from "vue-awesome-swiper";
import "swiper/swiper-bundle.css";
import { Navigation, Pagination, Autoplay } from "swiper";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { firestore, storage } from "/firebaseConfig";
import { incrementBannerClickCount } from "@/utils/write/banner";

export default {
  name: "LinkookLiveBanner",
  components: {
    Swiper,
    SwiperSlide,
  },
  data() {
    return {
      Navigation,
      Pagination,
      Autoplay,
      banners: [],
      loading: true,
    };
  },
  async created() {
    await this.fetchBanners();
  },
  methods: {
    /**
     * 배너 데이터 가져오기
     * 라이브 배너와 커스텀 배너를 모두 가져와서 병합
     */
    async fetchBanners() {
      try {
        this.loading = true;

        const [liveBanners, customBanners] = await Promise.all([
          this.fetchLiveBanners(),
          this.fetchCustomBanners(),
        ]);

        // 두 배너 목록 합치기 (null 값 제거)
        const allBanners = [...liveBanners, ...customBanners].filter(Boolean);

        // 우선순위로 정렬 (priority가 높을수록 먼저 표시)
        allBanners.sort((a, b) => {
          const priorityA = a.priority || 0;
          const priorityB = b.priority || 0;
          return priorityB - priorityA;
        });

        this.banners = allBanners;
      } catch (error) {
        console.error("배너 데이터 가져오기 실패:", error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * 라이브 배너 가져오기 (bannerOn이 true인 라이브)
     */
    async fetchLiveBanners() {
      const livesQuery = query(
        collection(firestore, "lives"),
        where("bannerOn", "==", true)
      );
      const livesSnapshot = await getDocs(livesQuery);

      return Promise.all(
        livesSnapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const bannerImgUrl = await this.getBannerImageUrl(
            data.banner,
            data.title
          );

          if (!bannerImgUrl) return null;

          return {
            id: docSnap.id,
            title: data.title,
            img: bannerImgUrl,
            date: data.date,
            place: data.place,
            link: data.link,
            type: data.type,
            dayCount: this.calculateDaysLeft(data.date),
            source: "live",
            priority: data.priority || 0, // 우선순위
          };
        })
      );
    },

    /**
     * 커스텀 배너 가져오기 (active가 true이고 type이 main인 배너)
     */
    async fetchCustomBanners() {
      const bannersQuery = query(
        collection(firestore, "banners"),
        where("active", "==", true),
        where("type", "==", "main")
      );
      const bannersSnapshot = await getDocs(bannersQuery);

      return Promise.all(
        bannersSnapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const bannerImgUrl = await this.getBannerImageUrl(
            data.img,
            data.title
          );

          if (!bannerImgUrl) return null;

          return {
            id: docSnap.id,
            title: data.title,
            img: bannerImgUrl,
            date: data.date || "",
            place: data.place || "",
            link: data.link || "",
            type: "site",
            dayCount: this.calculateDaysLeft(data.date),
            source: "custom",
            priority: data.priority || 0, // 우선순위
          };
        })
      );
    },

    /**
     * 배너 이미지 URL 가져오기
     * HTTP URL이면 그대로 반환, 아니면 Storage에서 다운로드 URL 가져오기
     */
    async getBannerImageUrl(imagePath, title) {
      if (!imagePath) {
        console.error("배너 이미지가 없습니다:", title);
        return null;
      }

      // 이미 HTTP URL인 경우 그대로 반환
      if (imagePath.startsWith("http")) {
        return imagePath;
      }

      // Storage에서 다운로드 URL 가져오기
      try {
        const imageRef = ref(storage, `banner/${imagePath}`);
        return await getDownloadURL(imageRef);
      } catch (error) {
        console.error("배너 이미지 로드 실패:", error);
        return null;
      }
    },

    /**
     * D-Day 계산
     */
    calculateDaysLeft(targetDate) {
      const substrDate = targetDate?.substring(0, 10);
      if (!substrDate) return "Invalid date";

      const now = new Date();
      const eventDate = parse(substrDate, "yyyy.MM.dd", new Date());

      if (isNaN(eventDate.getTime())) return "Invalid date";

      const nowMidnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate()
      );
      const eventMidnight = new Date(
        eventDate.getFullYear(),
        eventDate.getMonth(),
        eventDate.getDate()
      );

      const daysLeft = Math.ceil(
        (eventMidnight - nowMidnight) / (1000 * 60 * 60 * 24)
      );

      // 공연 당일까지는 D-Day로 표시
      return daysLeft === 0 ? "D-DAY" : daysLeft > 0 ? `D-${daysLeft}` : `종료`;
    },

    /**
     * 배너 클릭 핸들러
     */
    async handleBannerClick(banner) {
      // 클릭 횟수 증가
      await incrementBannerClickCount(banner);

      // 배너 타입에 따라 다른 동작 수행
      if (banner.source === "custom" && banner.link) {
        // 커스텀 배너: 외부 링크로 이동
        window.open(banner.link, "_blank");
      } else if (banner.source === "live") {
        // 라이브 배너: 상세 페이지로 이동
        this.goToDetail(banner);
      }
    },

    /**
     * 라이브 상세 페이지로 이동
     */
    goToDetail(banner) {
      this.$router.push({
        name: "LiveDetail",
        params: { slug: banner.slug || banner.id },
      });
    },
  },
};
</script>

<style module>
/* ================================================
   CONTAINER
   ================================================ */

.container {
  width: 90%;
  margin: 0px auto;
  min-height: 150px;
  padding: 10px;
}

/* ================================================
   BANNER
   ================================================ */

.bannerContainer {
  width: 100%;
  height: 100%;
  position: relative;
}

.bannerSwiper {
  width: 100%;
  height: 100%;
  border-radius: 10px;
}

.slide {
  width: 100%;
  height: 100%;
  position: relative;
}

.bannerImg {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 10px;
}

.dayCount {
  position: absolute;
  top: 5px;
  left: 5px;
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
  font-family: var(--font-noto-b);
  background-color: rgba(0, 0, 0, 0.5);
  padding: 4px 8px 5px;
  border-radius: 4px;
}

/* ================================================
   SKELETON UI
   ================================================ */

.skeletonBannerContainer {
  width: 100%;
  height: 100%;
  position: relative;
}

.skeletonBanner {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.skeletonBannerImg {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  border-radius: 8px;
  animation: skeletonShimmer 2s infinite;
}

@keyframes skeletonShimmer {
  0% {
    background-position: -200px 0;
  }

  100% {
    background-position: calc(200px + 100%) 0;
  }
}

/* ================================================
   반응형 디자인
   - Medium: 481px ~ (기본 스타일, 최대 1024px)
   - Small: ~ 480px
   ================================================ */

/* Small (~ 480px) */
@media screen and (max-width: 480px) {
  .container {
    width: 90%;
    margin: 4px auto;
    min-height: 100px;
    padding: 0px;
  }

  .bannerSwiper {
    border-radius: 6px;
  }

  .bannerImg {
    border-radius: 6px;
  }

  .dayCount {
    top: 4px;
    left: 4px;
    font-size: 0.75rem;
    padding: 3px 6px 4px;
    border-radius: 3px;
  }

  .skeletonBanner {
    border-radius: 6px;
  }

  .skeletonBannerImg {
    border-radius: 6px;
    background-size: 150px 100%;
  }
}
</style>
